import {Avatar, Tab, Tabs} from "@heroui/react";
import {Table, TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {addToast} from "@heroui/toast";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useLocation, useNavigate} from '@tanstack/react-router'
import React from "react";
import PButton from "../../../components/PButton.tsx";
import {BookingStatus, BookingStatusToText} from "../../../constants.ts";
import Header from "../../../Header.tsx";
import {useJwtUser} from "../../../JWTAuthProvider.tsx";
import ClubsView_List from "../../../players/ClubsView_List.tsx";
import {tableFullScreen} from "../../../players/PlayersView_Players.tsx";
import services from "../../../services";
import {Booking, Club} from "../../../types";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/courts-booking/$')({
    component: RouteComponent,
})

function RouteComponent() {
    const user = useJwtUser();
    const pageLocation = useLocation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const bookingsQuery = useQuery({
        queryKey: queryKeys.booking.getBookings({upcoming: true}),
        queryFn:  async () => services.booking.getBookings({upcoming: true}),
        enabled:  !!user?.uid,
    })

    const bookingsHistoricalQuery = useQuery({
        queryKey: queryKeys.booking.getBookings({upcoming: false}),
        queryFn:  async () => services.booking.getBookings({upcoming: false}),
        enabled:  !!user?.uid,
    })

    const cancelBooking = (booking_uid: string) => {
        const confirmed = window.confirm("Are you sure?");
        if (!confirmed) {
            return;
        }
        cancelBookingMut.mutate({booking_uid});
    }

    const cancelBookingMut = useMutation({
        mutationFn: async ({booking_uid}: any) => services.booking.cancel({
            uid: booking_uid,
        }),
        onSuccess() {
            addToast({
                color: "success",
                title: "Booking has been cancelled",
            })
            queryClient.invalidateQueries({queryKey: queryKeys.booking.getAvailableCourts()});
            queryClient.invalidateQueries({queryKey: queryKeys.booking.getBookings()});
        }
    })

    function StatusDescription(status, upcoming) {
        switch (status) {
            case BookingStatus.Created:
                return (upcoming ? 'Please wait for confirmation' : 'Was not approved');
            default:
                return BookingStatusToText(status);
        }
    }

    const startConversation = useMutation({
        mutationFn: async (club: Club) => services.conversations.storeToClub(club.uid),
        onError:    (error) => {
            addToast({
                title: error.message,
            });
        },
        onSuccess:  (data) => {
            navigate({
                to:     '/messages/',
                search: {
                    uid: data.uid,
                },
            });
        }
    });

    return <>
        <Header backUrl={`/profiles/${user?.uid}`} />

        <div className={"max-w-6xl w-11/12 mx-auto"}>

            <Tabs aria-label="Choose between Upcoming and Past bookings" color="primary" selectedKey={pageLocation.pathname} className="w-full" classNames={{
                tabList: "mx-auto",
            }}>
                <Tab title="Upcoming Bookings" key="/courts-booking/" href="/courts-booking/">
                    {!!bookingsQuery.data?.bookings.length && <>
                        {!!bookingsQuery.data &&
                            <Table classNames={tableFullScreen} isStriped={true}>
                                <TableHeader>
                                    <TableColumn>Court</TableColumn>
                                    <TableColumn>Time</TableColumn>
                                    <TableColumn>Status</TableColumn>
                                    <TableColumn>{' '}</TableColumn>
                                </TableHeader>
                                <TableBody>
                                    {bookingsQuery.data.bookings.map((booking: Booking) =>
                                        <TableRow key={booking.uid}>
                                            <TableCell>
                                                <div className="flex w-full">
                                                    <div>
                                                        <Avatar className={"mr-2"} src={booking.court.thumb_url || booking.court.image_url || booking.court.club.thumb_url || booking.court.club.image_url} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className={"text-medium"}>{booking.court.name}</div>
                                                        <div className={"text-small"}>{booking.court.club.name}</div>
                                                        <PButton size={"sm"} onPress={() => startConversation.mutate(booking.court.club)}>Send message</PButton>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {booking.start_date} {booking.start_time}<br />
                                                {booking.duration_minutes}min
                                            </TableCell>
                                            <TableCell>
                                                {StatusDescription(booking.status, true)}
                                            </TableCell>
                                            <TableCell>
                                                {booking.status !== BookingStatus.Cancelled && <>
                                                    <PButton onPress={() => cancelBooking(booking.uid)}>Cancel</PButton>
                                                </>}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        }
                    </>
                    }
                </Tab>
                <Tab title="Past Bookings" key="/courts-booking/historical/" href="/courts-booking/historical/">
                    {!!bookingsHistoricalQuery.data?.bookings.length && <>
                        {!!bookingsHistoricalQuery.data &&
                            <Table classNames={tableFullScreen} isStriped={true}>
                                <TableHeader>
                                    <TableColumn>Court</TableColumn>
                                    <TableColumn>Time</TableColumn>
                                    <TableColumn>Status</TableColumn>
                                </TableHeader>
                                <TableBody>
                                    {bookingsHistoricalQuery.data.bookings.map((booking: Booking | any) =>
                                        <TableRow key={booking.uid}>
                                            <TableCell>
                                                <div className="flex w-full">
                                                    <div>
                                                        <Avatar className={"mr-2"} src={booking.court.thumb_url || booking.court.image_url || booking.court.club.thumb_url || booking.court.club.image_url} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className={"text-medium"}>{booking.court.name}</div>
                                                        <div className={"text-small"}>{booking.court.club.name}</div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {booking.start_date} {booking.start_time}<br />
                                                {booking.duration_minutes}min
                                            </TableCell>
                                            <TableCell>{StatusDescription(booking.status, false)}</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        }
                    </>
                    }
                </Tab>
            </Tabs>

            <div className={"mt-4"}>
                <div className={"text-xl mb-2"}>Create Booking</div>
                <ClubsView_List />
            </div>
        </div>
    </>
}
